import {
  Alert,
  Anchor,
  Box,
  Button,
  CopyButton,
  Group,
  Paper,
  Skeleton,
  Stack,
  Text,
} from "@mantine/core";
import { IconCopy, IconCheck, IconLayoutGrid } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { LanguageTranslation } from "@models/translation";
import { languageLabel } from "@config/languages";
import { StatusBadge } from "@components/StatusBadge";
import { FeedbackControl } from "./FeedbackControl";

interface Props {
  translationId: string;
  lang: string;
  data?: LanguageTranslation;
  onShowModels: (lang: string) => void;
}

export const ResultCard: React.FC<Props> = ({ translationId, lang, data, onShowModels }) => {
  const { t } = useTranslation();
  const status = data?.status ?? "pending";
  const completed = status === "completed" && Boolean(data?.text);
  const failed = status === "failed";

  return (
    <Paper withBorder radius="md" p="md" styles={{ root: { borderWidth: "1.5px", borderColor: "var(--mantine-color-gray-4)" } }}>
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <Text fw={600} ff="heading">
            {languageLabel(lang)}
          </Text>
          <Text size="xs" c="dimmed" tt="uppercase">
            {lang}
          </Text>
        </Group>
        <StatusBadge status={status} />
      </Group>

      {!data && (
        <Stack gap={8}>
          <Skeleton height={12} radius="sm" />
          <Skeleton height={12} radius="sm" width="80%" />
          <Skeleton height={12} radius="sm" width="60%" />
        </Stack>
      )}

      {failed && (
        <Alert color="red" variant="light">
          {data?.error || t("translation.failedHint")}
        </Alert>
      )}

      {completed && (
        <>
          <Box
            p="md"
            style={{
              whiteSpace: "pre-wrap",
              lineHeight: 1.6,
              fontSize: "16px",
              background: "var(--mantine-color-gray-0)",
              borderRadius: "10px",
              border: "1.5px solid var(--mantine-color-gray-4)",
            }}
          >
            {data?.text}
          </Box>

          <Group justify="space-between" mt="md">
            <Group gap="xs">
              <CopyButton value={data?.text ?? ""} timeout={1500}>
                {({ copied, copy }) => (
                  <Button
                    size="xs"
                    variant="light"
                    color={copied ? "green" : "gray"}
                    leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                    onClick={copy}
                  >
                    {copied ? t("translation.copied") : t("translation.copy")}
                  </Button>
                )}
              </CopyButton>
              <Anchor component="button" size="xs" c="brand.7" onClick={() => onShowModels(lang)}>
                <Group gap={4} wrap="nowrap">
                  <IconLayoutGrid size={14} />
                  {t("translation.allModels")}
                </Group>
              </Anchor>
            </Group>
            <FeedbackControl translationId={translationId} targetLang={lang} />
          </Group>
        </>
      )}
    </Paper>
  );
};
